/**
 * Resolución de tenant por hostname — Structura Food Market.
 *
 * El sitio marketing se sirve en food.structura.com.ar.
 * Los tenants viven en <slug>.structura.com.ar (wildcard DNS).
 * También se acepta el esquema histórico <slug>.food.structura.com.ar.
 */

/** Hosts base de la plataforma que NO son subdominios de tenant. */
const PLATFORM_HOSTS = new Set([
  "structura.com.ar",
  "www.structura.com.ar",
  "food.structura.com.ar",
  "foodmarket.structura.com.ar",
  "app.structura.com.ar",
  // login.structura.com.ar, micuenta.structura.com.ar y saas.structura.com.ar
  // son alias de marca (ver netlify/edge-functions/login-host.ts y el nuevo
  // dominio público) — sin esto, tenant-host-slug.ts los leería como slug de
  // tenant en cada request.
  "login.structura.com.ar",
  "micuenta.structura.com.ar",
  "saas.structura.com.ar",
  "localhost",
  "127.0.0.1",
  "::1",
]);

/** Subdominios reservados que no pertenecen a un tenant. */
const RESERVED_SUBDOMAINS = new Set([
  "www", "app", "food", "foodmarket", "api", "admin", "mail", "cdn",
  // SCRUM-241: picking.structura.com.ar es la superficie de Structura Picking.
  // Sin reservarlo, el host se leería como el slug de un tenant llamado "picking"
  // y respondería un 404 de storefront.
  "picking",
  // Alias de marca (login/micuenta/SaaS público) — mismo motivo que "picking".
  "login", "micuenta", "saas",
]);

/** Zonas que albergan subdominios de tenant (canónica primero). */
const TENANT_ZONES = ["structura.com.ar", "food.structura.com.ar"];

export interface TenantHost {
  kind: "platform" | "subdomain" | "custom";
  subdomain?: string;
  host: string;
}

/** Normaliza un header host: minúsculas, sin puerto. */
export function normalizeHost(rawHost: string | null | undefined): string {
  if (!rawHost) return "";
  return rawHost.trim().toLowerCase().split(":")[0];
}

/**
 * Clasifica un host:
 *  - platform: dominio base de la plataforma → marketing
 *  - subdomain: <slug>.structura.com.ar (o histórico .food.structura.com.ar) → storefront
 *  - custom: dominio propio del tenant → resolver por DB
 */
export function parseTenantHost(rawHost: string | null | undefined): TenantHost {
  const host = normalizeHost(rawHost);
  if (!host || PLATFORM_HOSTS.has(host)) return { kind: "platform", host };

  for (const zone of TENANT_ZONES) {
    if (host === zone) return { kind: "platform", host };
    if (host.endsWith(`.${zone}`)) {
      const label = host.slice(0, host.length - zone.length - 1); // quita ".zone"
      const sub = label.split(".").filter(Boolean)[0] ?? ""; // primer label
      if (!sub || RESERVED_SUBDOMAINS.has(sub)) return { kind: "platform", host };
      return { kind: "subdomain", subdomain: sub, host };
    }
  }

  // No pertenece a las zonas de la plataforma → dominio propio del tenant
  return { kind: "custom", host };
}
